import { useState, useCallback, useRef } from 'react'
import {
  Shield,
  Upload,
  FileText,
  AlertTriangle,
  AlertCircle,
  Info,
  CheckCircle,
  Download,
  Copy,
  Save,
  Loader2,
  ChevronDown,
  ChevronRight,
  Clock,
  Trash2,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api, type SafeReviewResult, type SafeReviewListItem } from '../lib/api'
import { useDoctrineMode } from '../hooks/useDoctrineMode'
import { LiteModeNotice } from '../components/LiteModeNotice'

// ── Helpers ────────────────────────────────────────────────────────────────

const riskColors: Record<string, string> = {
  high: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30',
  medium: 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/30',
  low: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30',
  clear: 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30',
}

const riskLabels: Record<string, string> = {
  high: 'HIGH RISK',
  medium: 'MEDIUM RISK',
  low: 'LOW RISK',
  clear: 'CLEAR',
}

const severityIcons = {
  critical: AlertCircle,
  warning: AlertTriangle,
  info: Info,
} as const

const severityColors: Record<string, string> = {
  critical: 'text-red-600 dark:text-red-400 border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20',
  warning: 'text-yellow-600 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20',
  info: 'text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20',
}

const categoryColors: Record<string, string> = {
  S: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
  A: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
  F: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
  E: 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300',
}

// ── Component ──────────────────────────────────────────────────────────────

export function SafeReviewPage() {
  const { isLite, isFeatureAvailable, isRouteAvailable } = useDoctrineMode()
  if (isLite && (!isFeatureAvailable('safe-review') || !isRouteAvailable('safe-review'))) {
    return <LiteModeNotice feature="SAFE Review" />
  }
  return <SafeReviewPageInner />
}

function SafeReviewPageInner() {
  const [tab, setTab] = useState<'review' | 'history'>('review')
  const [content, setContent] = useState('')
  const [title, setTitle] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<SafeReviewResult | null>(null)
  const [history, setHistory] = useState<SafeReviewListItem[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [expandedFindings, setExpandedFindings] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)
  const [copySuccess, setCopySuccess] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Submit content for SAFE review
  const handleSubmit = useCallback(async () => {
    if (!content.trim()) return
    setLoading(true)
    setError(null)
    setResult(null)

    try {
      const res = await api.safeReview.run(content, title || undefined)
      setResult(res)
      // Expand all findings by default
      setExpandedFindings(new Set(res.findings.map((f) => f.id)))
    } catch (e: any) {
      setError(e.message || 'Failed to run SAFE review')
    } finally {
      setLoading(false)
    }
  }, [content, title])

  // Load review history
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true)
    try {
      const items = await api.safeReview.list()
      setHistory(items)
    } catch {
      setHistory([])
    } finally {
      setHistoryLoading(false)
    }
  }, [])

  // Load a past review
  const loadReview = useCallback(async (id: string) => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.safeReview.get(id)
      setResult(res)
      setExpandedFindings(new Set(res.findings.map((f) => f.id)))
      setTab('review')
    } catch (e: any) {
      setError(e.message || 'Failed to load review')
    } finally {
      setLoading(false)
    }
  }, [])

  // File upload handler
  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (ev) => {
      const text = ev.target?.result as string
      setContent(text)
      if (!title) setTitle(file.name.replace(/\.[^.]+$/, ''))
    }
    reader.readAsText(file)

    // Reset input so re-uploading same file works
    e.target.value = ''
  }, [title])

  // Download markdown report
  const handleDownload = useCallback(async () => {
    if (!result) return
    try {
      const mdUrl = api.safeReview.markdownUrl(result.id)
      const response = await fetch(mdUrl)
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `safe-review-${result.id.slice(0, 8)}.md`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      setError('Failed to download report')
    }
  }, [result])

  // Copy markdown to clipboard
  const handleCopy = useCallback(async () => {
    if (!result) return
    try {
      const mdUrl = api.safeReview.markdownUrl(result.id)
      const response = await fetch(mdUrl)
      const text = await response.text()
      await navigator.clipboard.writeText(text)
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)
    } catch {
      setError('Failed to copy report')
    }
  }, [result])

  // Save to documents/library
  const handleSave = useCallback(async () => {
    if (!result) return
    try {
      const mdUrl = api.safeReview.markdownUrl(result.id)
      const response = await fetch(mdUrl)
      const text = await response.text()

      // Save via editor endpoint
      await fetch('/api/editor/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          path: `documents/library/safe-reviews/safe-review-${result.id.slice(0, 8)}.md`,
          content: text,
        }),
      })
      setError(null)
      alert('Report saved to documents/library/safe-reviews/')
    } catch {
      // If editor save fails, offer download instead
      handleDownload()
    }
  }, [result, handleDownload])

  // Toggle finding expansion
  const toggleFinding = (id: string) => {
    setExpandedFindings((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
            <div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">SAFE Review</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Sensitive, Accurate, Financial, Effect — GitLab SAFE Framework compliance
              </p>
            </div>
          </div>

          {/* Tab Switcher */}
          <div className="flex gap-1 bg-gray-200 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => setTab('review')}
              className={clsx(
                'px-4 py-1.5 text-sm font-medium rounded-md transition-colors',
                tab === 'review'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              )}
            >
              Review
            </button>
            <button
              onClick={() => {
                setTab('history')
                loadHistory()
              }}
              className={clsx(
                'px-4 py-1.5 text-sm font-medium rounded-md transition-colors',
                tab === 'history'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              )}
            >
              History
            </button>
          </div>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="mx-6 mt-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}

      {tab === 'review' ? (
        <div className="p-6">
          {/* Input Section */}
          {!result && (
            <div className="max-w-4xl mx-auto space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Document Title (optional)
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Q2 Planning Blog Post"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              {/* Content Input */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Document Content
                  </label>
                  <div className="flex gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".md,.txt,.markdown"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <Upload className="w-4 h-4" />
                      Upload File
                    </button>
                  </div>
                </div>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Paste your document content here, or upload a .md / .txt file..."
                  rows={16}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono text-sm"
                />
                <p className="mt-1 text-xs text-gray-400">
                  {content.length > 0 ? `${content.length.toLocaleString()} characters` : 'Supports markdown and plain text'}
                </p>
              </div>

              {/* Submit */}
              <div className="flex justify-end">
                <button
                  onClick={handleSubmit}
                  disabled={loading || !content.trim()}
                  className={clsx(
                    'flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium text-white transition-colors',
                    loading || !content.trim()
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-indigo-600 hover:bg-indigo-700'
                  )}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Shield className="w-4 h-4" />
                      Run SAFE Review
                    </>
                  )}
                </button>
              </div>

              {/* SAFE Framework Key */}
              <div className="mt-8 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">SAFE Framework Reference</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { letter: 'S', name: 'Sensitive', desc: 'No confidential company data, customer info, or MNPI without Legal approval' },
                    { letter: 'A', name: 'Accurate', desc: 'All claims verifiable, citable, DRI-approved; estimates marked with "as of" dates' },
                    { letter: 'F', name: 'Financial', desc: 'No financials, guidance, or derivable metrics without CFO approval' },
                    { letter: 'E', name: 'Effect', desc: 'Consider intended and unintended effects; information not viewed in a silo' },
                  ].map((item) => (
                    <div key={item.letter} className="flex gap-3">
                      <span className={clsx('flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm', categoryColors[item.letter])}>
                        {item.letter}
                      </span>
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{item.name}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{item.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Results Section */}
          {result && (
            <div className="max-w-4xl mx-auto space-y-6">
              {/* Result Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{result.title}</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Reviewed {new Date(result.reviewedAt).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setResult(null)
                      setContent('')
                      setTitle('')
                    }}
                    className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    New Review
                  </button>
                  <button
                    onClick={handleCopy}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <Copy className="w-4 h-4" />
                    {copySuccess ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={handleDownload}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                  <button
                    onClick={handleSave}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
                  >
                    <Save className="w-4 h-4" />
                    Save
                  </button>
                </div>
              </div>

              {/* Risk Badge + Summary */}
              <div className={clsx('p-4 rounded-lg border', riskColors[result.overallRisk])}>
                <div className="flex items-center gap-3 mb-2">
                  {result.overallRisk === 'clear' ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <AlertTriangle className="w-5 h-5" />
                  )}
                  <span className="font-bold text-sm uppercase tracking-wide">
                    {riskLabels[result.overallRisk]}
                  </span>
                </div>
                <p className="text-sm">{result.summary}</p>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard label="Total Findings" value={result.stats.total} />
                <StatCard label="Critical" value={result.stats.critical} color="text-red-600 dark:text-red-400" />
                <StatCard label="Warning" value={result.stats.warning} color="text-yellow-600 dark:text-yellow-400" />
                <StatCard label="Info" value={result.stats.info} color="text-blue-600 dark:text-blue-400" />
              </div>

              {/* Category Breakdown */}
              <div className="grid grid-cols-4 gap-2">
                {(['S', 'A', 'F', 'E'] as const).map((cat) => (
                  <div key={cat} className={clsx('px-3 py-2 rounded-lg text-center', categoryColors[cat])}>
                    <div className="text-lg font-bold">{result.stats.byCategory[cat]}</div>
                    <div className="text-xs font-medium">
                      {{ S: 'Sensitive', A: 'Accurate', F: 'Financial', E: 'Effect' }[cat]}
                    </div>
                  </div>
                ))}
              </div>

              {/* Findings List */}
              {result.findings.length === 0 ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
                  <p className="text-lg font-medium">No issues found</p>
                  <p className="text-sm">Document appears SAFE for external sharing.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
                    Findings ({result.findings.length})
                  </h3>
                  {result.findings.map((f) => {
                    const SevIcon = severityIcons[f.severity as keyof typeof severityIcons] ?? AlertTriangle
                    const isExpanded = expandedFindings.has(f.id)
                    return (
                      <div
                        key={f.id}
                        className={clsx(
                          'border rounded-lg overflow-hidden',
                          severityColors[f.severity]
                        )}
                      >
                        <button
                          onClick={() => toggleFinding(f.id)}
                          className="w-full px-4 py-3 flex items-center gap-3 text-left"
                        >
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 flex-shrink-0" />
                          ) : (
                            <ChevronRight className="w-4 h-4 flex-shrink-0" />
                          )}
                          <SevIcon className="w-4 h-4 flex-shrink-0" />
                          <span className={clsx('text-xs font-bold px-1.5 py-0.5 rounded', categoryColors[f.category])}>
                            {f.category}
                          </span>
                          <span className="text-sm font-medium flex-1">{f.issue}</span>
                          <span className="text-xs opacity-70">{f.location}</span>
                        </button>

                        {isExpanded && (
                          <div className="px-4 pb-4 space-y-3 border-t border-current/10">
                            <div className="pt-3">
                              <div className="text-xs font-semibold uppercase tracking-wide opacity-70 mb-1">
                                Flagged Text
                              </div>
                              <blockquote className="text-sm italic border-l-2 border-current/30 pl-3 py-1">
                                {f.excerpt}
                              </blockquote>
                            </div>
                            <div>
                              <div className="text-xs font-semibold uppercase tracking-wide opacity-70 mb-1">
                                Location
                              </div>
                              <p className="text-sm">{f.location}</p>
                            </div>
                            <div>
                              <div className="text-xs font-semibold uppercase tracking-wide opacity-70 mb-1">
                                Suggested Action
                              </div>
                              <p className="text-sm">{f.suggestion}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Token Usage */}
              <div className="text-xs text-gray-400 dark:text-gray-500 text-right">
                Tokens: {result.inputTokens.toLocaleString()} in / {result.outputTokens.toLocaleString()} out
              </div>
            </div>
          )}
        </div>
      ) : (
        /* History Tab */
        <div className="p-6">
          <div className="max-w-4xl mx-auto">
            {historyLoading ? (
              <div className="flex items-center justify-center py-12 text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Loading history...
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No reviews yet. Run your first SAFE review to see it here.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {history.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => loadReview(item.id)}
                    className="w-full text-left p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center gap-4"
                  >
                    <div className={clsx('px-2 py-1 rounded text-xs font-bold uppercase', riskColors[item.overallRisk])}>
                      {item.overallRisk}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {item.title}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <Clock className="w-3 h-3" />
                        {new Date(item.reviewedAt).toLocaleString()}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {item.total} findings
                      </div>
                      {item.critical > 0 && (
                        <div className="text-xs text-red-600 dark:text-red-400">
                          {item.critical} critical
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Stat Card ──────────────────────────────────────────────────────────────

function StatCard({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className={clsx('text-2xl font-bold', color || 'text-gray-900 dark:text-white')}>
        {value}
      </div>
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
    </div>
  )
}
